import { useMemo, useState } from "react"
import styled from "styled-components"

import { type AnalyticsInformation } from "@forento/core/models/analytics"
import { getFirstDayOfWeek } from "@forento/core/utilities/date"
import dayjs from "@forento/core/utilities/dayjs"
import { exhaustiveGuard } from "@forento/core/utilities/switch"

import { SalesIcon, UsersIcon } from "~/components/Icon"
import Layout, { PageTitle } from "~/components/Layout"
import PartialLoadingPage from "~/components/PartialLoadingPage"
import { query } from "~/utilities/trpc"

import BusinessInsights from "./BusinessInsights"
import Chart from "./Chart"
import { type ChartCategory } from "./ChartCategorySelector"
import Devices from "./Devices"
import PeriodSelector, { type Period, periodOptions } from "./PeriodSelector"
import Sources from "./Sources"
import StatCard from "./StatCard"

type ChartInterval = "daily" | "monthly"
type AnalyticsInformationWithVisits = Omit<AnalyticsInformation, "visits"> & {
	visits: NonNullable<AnalyticsInformation["visits"]>
}

function getNormalizedChartData(selectedCategory: ChartCategory, data: AnalyticsInformationWithVisits) {
	switch (selectedCategory) {
		case "sales":
			return {
				label: "sales",
				unit: data.sales.days[0]?.currency ?? "USD",
				values: data.sales.days.map(x => ({ date: dayjs(x.date), value: x.amount })),
			}
		case "visitors":
			return {
				label: "visitors",
				values: data.visits.days.map(x => ({ date: dayjs(x.date), value: x.visits })),
			}
		case "unique-visitors":
			return {
				label: "unique visitors",
				values: data.visits.days.map(x => ({ date: dayjs(x.date), value: x.uniqueVisitors })),
			}
	}
}

function getDataSpecificChartProps(
	data: AnalyticsInformationWithVisits,
	category: ChartCategory,
	interval: ChartInterval,
): { label: string; unit?: string; values: { name: string; value: number }[] } {
	const analytics = getNormalizedChartData(category, data)

	switch (interval) {
		case "daily":
			return {
				...analytics,
				values: analytics.values.map(value => ({ name: value.date.format("DD/MM"), value: value.value })),
			}
		case "monthly":
			return {
				...analytics,
				values: analytics.values.reduce<{ name: string; value: number }[]>((value, current) => {
					const existing = value.find(x => x.name === current.date.format("YYYY-MM"))
					return [
						...value.filter(x => x.name !== existing?.name),
						{ name: current.date.format("YYYY-MM"), value: (existing?.value ?? 0) + current.value },
					]
				}, []),
			}
	}
}

const AnalyticsPage: React.FC = () => {
	const [selectedPeriod, setSelectedPeriod] = useState<Period>("this-week")
	const [selectedChartCategory, setSelectedChartCategory] = useState<ChartCategory>("sales")

	const periodStartDate = useMemo(() => {
		switch (selectedPeriod) {
			case "today":
				return dayjs()
			case "this-week":
				return dayjs(getFirstDayOfWeek())
			case "7-days":
				return dayjs().subtract(7, "day")
			case "this-month":
				return dayjs(dayjs().format("YYYY-MM-01"))
			case "30-days":
				return dayjs().subtract(30, "day")
			case "this-year":
				return dayjs(dayjs().format("YYYY-01-01"))
			case "1-year":
				return dayjs().subtract(1, "year")
			case "all-time":
				return null
			default:
				exhaustiveGuard(selectedPeriod)
		}
	}, [selectedPeriod])

	const { data, error } = query.analytics.get.useQuery({ startDate: periodStartDate?.format("YYYY-MM-DD") ?? null })

	const selectedChartInterval: ChartInterval = (() => {
		if (["today", "this-week", "7-days", "this-month", "30-days"].includes(selectedPeriod)) return "daily"
		else return "monthly"
	})()

	return (
		<Layout>
			<PageTitle>Analytics</PageTitle>
			<PeriodSelector selectedValue={selectedPeriod} onChange={setSelectedPeriod} />
			{error ? (
				<Error>Failed to load analytics.</Error>
			) : data === undefined ? (
				<PartialLoadingPage />
			) : data.visits === null ? (
				<Error>
					Analytics can't be loaded right now. Usually this is because of routine maintenance. Please check
					back soon again. We're sorry for the inconvenience.
				</Error>
			) : (
				<>
					<Content>
						<MainContent>
							<StatCards>
								<StatCard
									gridArea="sales"
									icon={SalesIcon}
									value={data.sales.total}
									unit={data.sales.days[0]?.currency ?? "USD"}
									label="Sales"
								/>
								<StatCard
									gridArea="visitors"
									icon={UsersIcon}
									value={data.visits.days.reduce((value, current) => value + current.visits, 0)}
									label="Visitors"
								/>
							</StatCards>
							<Chart
								{...getDataSpecificChartProps(
									{ ...data, visits: data.visits },
									selectedChartCategory,
									selectedChartInterval,
								)}
								periodTitle={periodOptions.find(p => p.id === selectedPeriod)?.label ?? "Total"}
								selectedChartCategory={selectedChartCategory}
								setSelectedChartCategory={setSelectedChartCategory}
							/>
						</MainContent>
						<SideContent>
							<Sources sources={data.visits.sources} />
							<Devices devices={data.visits.devices} />
						</SideContent>
					</Content>
					<BusinessInsights data={data.business} />
				</>
			)}
		</Layout>
	)
}

const Error = styled.p`
	margin-top: 20px;
`

const Content = styled.div`
	display: grid;
	grid-template-columns: repeat(3, minmax(0, 1fr));
	gap: 20px;
	align-items: start;
	margin-top: 20px;

	@media (max-width: 1000px) {
		grid-template-columns: 1fr;
	}
`

const MainContent = styled.div`
	display: flex;
	grid-column: span 2;
	min-width: 0;
	flex-direction: column;
	gap: 20px;

	@media (max-width: 1000px) {
		grid-column: auto;
	}
`

const StatCards = styled.div`
	display: flex;
	gap: 20px;

	> * {
		min-width: 0;
		flex: 1;
	}

	@media (max-width: 600px) {
		flex-direction: column;
	}
`

const SideContent = styled.div`
	display: flex;
	min-width: 0;
	flex-direction: column;
	gap: 20px;
`

export default AnalyticsPage
